import {
    ESCAPE_KEYBOARD_LISTENER_ID,
    FRAME_INIT_MESSAGE_TYPE,
    KeyDownListenerId,
    MAIN_LISTENER_ID,
    Message,
    MessageListenerId,
    MessageType,
    METADATA_CHANGE_MESSAGE_TYPE,
    K_KEYBOARD_LISTENER_ID,
    PLAYER_CLOSE_MESSAGE_TYPE,
    PLAYER_INTERSECTION_CHANGE_MESSAGE_TYPE,
    PLAYER_OPEN_MESSAGE_TYPE,
    PlayerIntersectionChangeMessagePayload,
    VIDEO_RENDER_LISTENER_ID,
    SPACE_KEYBOARD_LISTENER_ID,
    PAGE_SCROLL_REQUEST_MESSAGE_TYPE,
    PageScrollMessagePayload,
    PLAYER_CLOSE_REQUEST_MESSAGE_TYPE,
    ACTION_BUTTON_CLICK_MESSAGE_TYPE,
} from './type/communication.type';
import {
    FlowConfigPlayerVideo,
    FlowConfigPlayerVideoButton,
    FlowConfigType,
    HIDE_PLAYER_ACTION_TYPE,
    OPEN_VIDEO_ACTION_TYPE,
} from './type/flow.type';
import { PageMetadata, PlayerState } from './type/store.type';

import { getIcon } from './util/getIcon.util';
import { getIsReducedMotion } from './util/getIsReducedMotion';
import { logDebug } from './util/logDebug.util';
import { matchTemplateStr } from './util/matchTemplateStr.util';
import { getEventTracker, initializeEventTracker } from './util/analytics.util';
import { getIsContentWrapped } from './util/getIsContentWrapped';
import { normalizeUrl } from './util/normalizeUrl.util';
import { hash } from './util/hash.util';

// NOTE: Selectors
const HISTORY_CONTAINER_SELECTOR = 'header .history-container';
const TITLE_SELECTOR = 'header h2';
const BADGE_SELECTOR = 'header .title-container__badge';
const BADGE_INITIALS_SELECTOR = 'header .title-container__badge-initials';
const BADGE_INITIALS_TEXT_SELECTOR =
    'header .title-container__badge-initials-text';
const BADGE_INITIALS_VERIFIED_SELECTOR =
    'header .title-container__badge-initials-verified';
const BADGE_EXTRA_SELECTOR = 'header .title-container__badge-extra';
const BADGE_EXTRA_CONTENT_SELECTOR =
    'header .title-container__badge-extra-content';
const BADGE_EXTRA_NAME_SELECTOR = 'header .title-container__badge-extra-name';
const BADGE_EXTRA_ROLE_SELECTOR = 'header .title-container__badge-extra-role';
const VIDEO_SELECTOR = '.video-element';
const TRACK_SELECTOR = '#player-video track';
const BUTTON_CONTAINER_SELECTOR = '.button-container';
const CAPTION_TEXT_SELECTOR = '.caption-text';
const CONTROL_CLOSE_SELECTOR = '.control-button__close';
const CONTROL_MUTE_SELECTOR = '.control-button__mute';
const CONTROL_GO_BACK_SELECTOR = '.control-button__goback';
const CONTROLS_CONTAINER_SELECTOR = '.controls-container';
const LOADER_CONTAINER_SELECTOR = '.loader-container';
const REPLAY_CONTAINER_SELECTOR = '.replay-container';
const NAME_PLATE_CONTAINER_SELECTOR = '.name-plate-container';
const NAME_PLATE_AUTHOR_SELECTOR = '.name-plate-container__author';
const NAME_PLATE_ROLE_SELECTOR = '.name-plate-container__role';
const NAME_PLATE_AUTHOR_TEXT_SELECTOR = '.name-plate-container__author-text';
const NAME_PLATE_ROLE_TEXT_SELECTOR = '.name-plate-container__role-text';
const PREVIEW_LABEL_SELECTOR = '.preview-label';
const LOGO_SELECTOR = '.logo';
const FOOTER_SELECTOR = 'footer';
const BRAND_SPACE_SELECTOR = '.brand-space';
const SPEED_INDICATOR_SELECTOR = '.speed-indicator';

// NOTE: Storage keys
const PLAYER_STATE_LOCAL_STORAGE_KEY = 'reelflow_player_state';
const VIEWED_NAMES_SESSION_STORAGE_KEY = 'reelflow_viewed_names';

// NOTE: Workspace Data
const workspace = window.__reelflowData.workspace;
const flowConfig = window.__reelflowData.flowConfig;
const flowConfigPlayer = flowConfig.player;

const isMuteStateChangedMap: Record<string, boolean> = {};
let pageMetadata: PageMetadata;
let playerStateMap: Record<string, PlayerState> = {};

const messageHistory: { message: Message; receivedAt: number }[] = [];
const messageEventListenerMap: {
    [key in MessageListenerId]?: (message: Message<any>) => void;
} = {};

const keyDownListenerMap: {
    [key in KeyDownListenerId]?: (event: KeyboardEvent) => void;
} = {};

function initializePlayerState() {
    var flowId = flowConfig.code;
    var type = flowConfig.type;
    const workspaceId = workspace.code;

    var cachedPlayerStateMap = null;

    try {
        var rawCachedPlayerStateMap = localStorage.getItem(
            PLAYER_STATE_LOCAL_STORAGE_KEY,
        );

        if (rawCachedPlayerStateMap) {
            cachedPlayerStateMap = JSON.parse(rawCachedPlayerStateMap)[
                workspaceId
            ];
        }
    } catch (ex) {}

    playerStateMap[flowId] = {
        currentVideoId: flowConfigPlayer.videos[0].id,
        videoIdHistory: [flowConfigPlayer.videos[0].id],
        controlMap: { isMuted: true, isPlaying: false },
        extra: {
            currentTime: 0,
            duration: 0,
            uiElementsHidden: false,
            isButtonListAnimated: false,
        },
    };

    if (cachedPlayerStateMap && type in cachedPlayerStateMap) {
        playerStateMap[flowId].controlMap.isMuted = cachedPlayerStateMap[type];

        isMuteStateChangedMap[flowId] = true;
    }
}

function cachePlayerMuteState() {
    const flowId = flowConfig.code;
    const type = flowConfig.type;
    const workspaceId = workspace.code;

    try {
        const currentPlayerStateMap = JSON.parse(
            localStorage.getItem(PLAYER_STATE_LOCAL_STORAGE_KEY) || '{}',
        );

        currentPlayerStateMap[workspaceId] = {
            ...(currentPlayerStateMap[workspaceId] || {}),
            [type]: playerStateMap[flowId].controlMap.isMuted,
        };

        localStorage.setItem(
            PLAYER_STATE_LOCAL_STORAGE_KEY,
            JSON.stringify(currentPlayerStateMap),
        );
    } catch (ex) {
        getEventTracker().track('error', {
            name: 'player_state_cache',
            // @ts-expect-error
            message: (ex && ex.message) || '',
        });
    }
}

function setIsNameViewed(name: string, role: string): void {
    try {
        let viewedNames: string[] = [];

        const rawViewedName = sessionStorage.getItem(
            VIEWED_NAMES_SESSION_STORAGE_KEY,
        );

        if (rawViewedName) {
            viewedNames = JSON.parse(rawViewedName);
        }

        const nameHash = hash((name || '') + (role || ''));

        if (!viewedNames.includes(nameHash)) {
            viewedNames.push(nameHash);
        }

        sessionStorage.setItem(
            VIEWED_NAMES_SESSION_STORAGE_KEY,
            JSON.stringify(viewedNames),
        );
    } catch (ex) {
        logDebug('[ReelFlow][setIsNameViewed] Error', ex);
    }
}

function getIsNameViewed(name: string, role: string): boolean {
    try {
        let viewedNames: string[] = [];

        const rawViewedName = sessionStorage.getItem(
            VIEWED_NAMES_SESSION_STORAGE_KEY,
        );

        if (rawViewedName) {
            viewedNames = JSON.parse(rawViewedName);
        }

        const nameHash = hash((name || '') + (role || ''));

        return viewedNames.includes(nameHash);
    } catch (ex) {
        return false;
    }
}

function renderVideo() {
    // TODO: Use "flowConfig" and get rid of "flow
    var flowState = playerStateMap[flowConfig.code];

    const eventTracker = getEventTracker();

    let isVideoStartedTracked = false;
    let isVideoMouseDownEffectRunning = false;
    let autoPlayerTimers: NodeJS.Timeout[] = [];
    let videoMouseDownTimerId: NodeJS.Timeout | null = null;

    var historyContainerNode = document.querySelector<HTMLElement>(
        HISTORY_CONTAINER_SELECTOR,
    );
    var titleNode = document.querySelector<HTMLElement>(TITLE_SELECTOR);
    var badgeNode = document.querySelector<HTMLElement>(BADGE_SELECTOR);
    var badgeInitialsNode = document.querySelector<HTMLElement>(
        BADGE_INITIALS_SELECTOR,
    );
    var badgeInitialsTextNode = document.querySelector<HTMLElement>(
        BADGE_INITIALS_TEXT_SELECTOR,
    );
    const badgeInitialsVerifiedNode = document.querySelector<HTMLElement>(
        BADGE_INITIALS_VERIFIED_SELECTOR,
    );
    var badgeExtraNode =
        document.querySelector<HTMLElement>(BADGE_EXTRA_SELECTOR);
    var badgeExtraContentNode = document.querySelector<HTMLElement>(
        BADGE_EXTRA_CONTENT_SELECTOR,
    );
    var badgeExtraNameNode = document.querySelector<HTMLElement>(
        BADGE_EXTRA_NAME_SELECTOR,
    );
    var badgeExtraRoleNode = document.querySelector<HTMLElement>(
        BADGE_EXTRA_ROLE_SELECTOR,
    );
    let videoPlaceholderNode =
        document.querySelector<HTMLVideoElement>(VIDEO_SELECTOR);
    const closeButtonNode = document.querySelector<HTMLButtonElement>(
        CONTROL_CLOSE_SELECTOR,
    );
    const muteButtonNode = document.querySelector<HTMLButtonElement>(
        CONTROL_MUTE_SELECTOR,
    );
    const goBackButtonNode = document.querySelector<HTMLButtonElement>(
        CONTROL_GO_BACK_SELECTOR,
    );
    const buttonContainerNode = document.querySelector<HTMLElement>(
        BUTTON_CONTAINER_SELECTOR,
    );
    const captionTextNode = document.querySelector<HTMLElement>(
        CAPTION_TEXT_SELECTOR,
    );
    const loaderContainerNode = document.querySelector<HTMLElement>(
        LOADER_CONTAINER_SELECTOR,
    );
    const replayContainerNode = document.querySelector<HTMLDivElement>(
        REPLAY_CONTAINER_SELECTOR,
    );
    const previewLabelNode = document.querySelector<HTMLDivElement>(
        PREVIEW_LABEL_SELECTOR,
    );
    const logoNode = document.querySelector<HTMLAnchorElement>(LOGO_SELECTOR);
    const brandSpaceNode =
        document.querySelector<HTMLDivElement>(BRAND_SPACE_SELECTOR);
    const footerNode = document.querySelector<HTMLElement>(FOOTER_SELECTOR);

    if (logoNode && workspace.url) {
        const utmQuery = `utm_medium=referral&utm_campaign=ReelFlowPlayer&utm_source=${encodeURIComponent(
            workspace.url,
        )}`;
        logoNode.href = `https://reelflow.com?${utmQuery}`;
    }

    // NOTE: The script needs these nodes to work, so we check if they are all in place
    const uiNodes = [
        historyContainerNode,
        titleNode,
        videoPlaceholderNode,
        closeButtonNode,
        muteButtonNode,
        goBackButtonNode,
        buttonContainerNode,
        captionTextNode,
        loaderContainerNode,
        replayContainerNode,
        previewLabelNode,
        footerNode,
        brandSpaceNode,
    ];

    // NOTE: This condition looks better but TS doesn't understand it and shows 'possibly null' errors
    // NOTE: If at least one of mandatory nodes doesn't exist prevent page from being rendered
    // if (uiNodes.includes(null)) {
    //     return;
    // }

    if (
        !historyContainerNode ||
        !titleNode ||
        !badgeNode ||
        !videoPlaceholderNode ||
        !closeButtonNode ||
        !muteButtonNode ||
        !goBackButtonNode ||
        !buttonContainerNode ||
        !captionTextNode ||
        !loaderContainerNode ||
        !replayContainerNode ||
        !footerNode ||
        !brandSpaceNode
    ) {
        logDebug("[ReelFlow] Mandatory node doesn't exist", uiNodes);

        return;
    }

    let buttonListShowAnimExecutor: Animation | null = null;

    const isBrandingHidden = flowConfigPlayer.brandingVariant === 4;

    const brandingVariant = flowConfigPlayer.brandingVariant || 1;

    if (isBrandingHidden) {
        brandSpaceNode.innerHTML = '';
        brandSpaceNode.classList.add('brand-space-hidden');
        footerNode.classList.add('footer__compacted');
    } else {
        if (brandingVariant === 1) {
            brandSpaceNode.classList.add('brand-space__variant-1');
        } else if (brandingVariant === 2) {
            brandSpaceNode.classList.add('brand-space__variant-2');
        }
    }

    Object.keys(flowConfigPlayer?.style?.footer ?? {}).forEach((styleKey) => {
        // @ts-expect-error
        footerNode.style[styleKey] = flowConfigPlayer.style.footer[styleKey];
    });

    // NOTE: Replacing the video placeholder with an actual video,
    //   so that we attach the listeners before rendering an element
    let videoNode = document.createElement('video');
    let trackNode = document.createElement('track');

    videoNode.classList.add('video-element');

    videoNode.src = flowConfigPlayer.videos[0].url;
    videoNode.role = 'video';
    videoNode.ariaLabel = 'Video Content';
    videoNode.crossOrigin = 'anonymous';
    videoNode.autoplay = false;
    videoNode.loop = false;
    videoNode.defaultMuted = true;
    videoNode.muted = true;
    videoNode.disablePictureInPicture = true;
    videoNode.disableRemotePlayback = true;
    videoNode.playsInline = true;
    videoNode.volume = 0.5;
    videoNode.playbackRate = flowConfigPlayer.videos[0].playbackRate || 1;

    Object.keys(flowConfigPlayer?.style?.video ?? {}).forEach((styleKey) => {
        // @ts-expect-error
        videoNode.style[styleKey] = flowConfigPlayer.style.video[styleKey];
    });

    trackNode.src = flowConfigPlayer.videos[0].subtitles;
    trackNode.label = 'English';
    trackNode.kind = 'subtitles';
    trackNode.srclang = 'en';
    trackNode.default = false;

    videoNode.appendChild(trackNode);

    //
    // NOTE: By default the element is hidden. Show only when the page is in preview mode
    //
    if (pageMetadata.isPreviewMode) {
        previewLabelNode?.classList.remove('hidden');
        brandSpaceNode.classList.add('hidden');
    }

    function onTrackCueChange(track: TextTrack) {
        if (!captionTextNode) {
            return;
        }

        if (track.activeCues && track.activeCues.length > 0) {
            // @ts-expect-error
            captionTextNode.innerHTML = track.activeCues[0].text;
            captionTextNode.style.opacity = '1';
        } else {
            captionTextNode.style.opacity = '0';
        }
    }

    function renderButton(button: FlowConfigPlayerVideoButton) {
        var elementType =
            button.actionType === OPEN_VIDEO_ACTION_TYPE ||
            button.actionType === HIDE_PLAYER_ACTION_TYPE
                ? 'button'
                : ['a', 'button', 'input'].includes(button.actionType)
                  ? button.actionType
                  : 'button';

        var element = document.createElement(elementType);

        element.classList.add('action-button');

        if (!button.style) {
            button.style = {};
        }

        // Combine styles for all buttons (i.e. "flowConfig.style.videoButton"), and
        //   styles defined for button individually (i.e. "button.style")
        var buttonStyle = Object.assign(
            {},
            flowConfigPlayer.style.videoButton,
            button.style,
        );

        if (buttonStyle) {
            Object.keys(buttonStyle).forEach(function (styleKey) {
                // @ts-expect-error
                element.style[styleKey] = buttonStyle[styleKey];
            });
        }

        if (
            button.actionType !== OPEN_VIDEO_ACTION_TYPE &&
            button.actionType !== HIDE_PLAYER_ACTION_TYPE
        ) {
            var customAttributes = ['hidePlayerOnNavigate'];

            Object.keys(button.actionPayload).forEach((attrName) => {
                if (customAttributes.includes(attrName)) {
                    return;
                }

                element.setAttribute(
                    attrName,
                    // @ts-expect-error
                    button.actionPayload[attrName],
                );
            });
        }

        element.setAttribute('data-action-id', button.id);

        element.innerHTML = button.label;

        buttonContainerNode?.append(element);
    }

    function renderHistoryBar() {
        var videoIds = flowState.videoIdHistory;
        var currentId = flowState.currentVideoId;

        var currentVideoData = flowConfigPlayer.videos.find(
            (videoItem) => videoItem.id === currentId,
        );

        if (!currentVideoData) {
            logDebug('[ReelFlow] Cannot get current video data', { currentId });

            return;
        }

        var currentHistoryContainer = document.querySelector(
            HISTORY_CONTAINER_SELECTOR,
        );

        if (currentHistoryContainer) {
            // currentHistoryContainer.remove();
        }

        if (!titleNode || !currentHistoryContainer) {
            return;
        }

        var historyElement = document.createElement('div');

        historyElement.classList.add('history-container');

        titleNode.innerHTML = matchTemplateStr(
            currentVideoData.title,
            pageMetadata.url,
        );

        var activeVideoIndex = videoIds.findIndex(
            (itemId) => itemId === currentId,
        );

        for (var i = 0; i < videoIds.length; i++) {
            var videoId = videoIds[i];
            var isFinished = i < activeVideoIndex;
            var isLast = i === videoIds.length - 1;
            var isActive = videoId === currentId;

            var historyItemElement = document.createElement('button');

            historyItemElement.classList.add('history-button');

            if (isLast) {
                historyItemElement.classList.add('history-button__last');
            }

            historyItemElement.role = 'slider';
            historyItemElement.ariaValueMin = '0';
            historyItemElement.ariaValueMax = '100';
            historyItemElement.ariaValueNow = isFinished ? '100' : '0';
            historyItemElement.ariaLabel = 'Video timeline';

            historyItemElement.setAttribute('data-video-id', videoId);

            var progressBg = document.createElement('div');

            progressBg.classList.add('history-button-progress-bg');

            if (isFinished) {
                progressBg.classList.add(
                    'history-button-progress-bg__finished',
                );
            }

            if (isActive) {
                var progressElement = document.createElement('div');

                progressElement.classList.add('history-button-progress-line');

                // TODO: Use CSS instead of JS re-renders

                progressBg.append(progressElement);
            }

            historyItemElement.append(progressBg);
            historyElement.append(historyItemElement);
        }

        currentHistoryContainer.replaceWith(historyElement);

        // There is no need to remove the previous event listener as
        //   the element it is attached to is removed from the DOM when the title is re-rendered.
        historyElement.addEventListener('click', function (event) {
            var targetButton = event.target;

            if (!targetButton) {
                return;
            }

            // @ts-expect-error
            var videoId = targetButton.getAttribute('data-video-id');

            if (
                !videoId
                // Clicking on the segment for the currently playing reel should restart that reel
                /* || videoId === flowState.currentVideoId */
            ) {
                return;
            }

            var videoData = flowConfigPlayer.videos.find(
                (item) => item.id === videoId,
            );

            if (!videoData) {
                return;
            }

            // @ts-expect-error
            var targetButtonRect = targetButton.getBoundingClientRect();

            var progress =
                (event.x - targetButtonRect.x) / targetButtonRect.width;

            if (videoId === flowState.currentVideoId) {
                if (!Number.isNaN(progress) && progress < 0.98) {
                    videoNode.currentTime = flowState.extra.duration * progress;

                    if (!flowState.controlMap.isPlaying) {
                        videoNode.muted = flowState.controlMap.isMuted;

                        // setMuteButtonIcon(videoNode.muted);

                        videoNode.play();
                    }
                }
            } else {
                replaceCurrentVideo(videoData);
            }
        });
    }

    function renderBadge(data: FlowConfigPlayerVideo) {
        if (
            !badgeInitialsTextNode ||
            !badgeExtraNameNode ||
            !badgeExtraRoleNode ||
            !badgeInitialsVerifiedNode
        ) {
            return;
        }

        if (data?.authorName && data?.authorRole) {
            badgeNode?.classList.remove('hidden');
            badgeNode?.classList.remove('title-container__badge--no-hover');
            badgeInitialsVerifiedNode?.classList.remove(
                'badge-initials-verified__centered',
            );
            badgeExtraNameNode.classList.add('badge-extra-name__bold');

            const [firstName, lastName] = data.authorName.split(' ');

            badgeInitialsTextNode.innerHTML =
                firstName.charAt(0) + (lastName?.charAt(0) || '');

            badgeExtraNameNode.innerHTML = data.authorName;
            badgeExtraRoleNode.innerHTML = data.authorRole;

            // Make badge focusable
            badgeNode?.setAttribute('tabindex', '0');
            badgeNode?.setAttribute('role', 'button');
            badgeNode?.setAttribute(
                'aria-label',
                `${data.authorName}, ${data.authorRole}`,
            );
        } else {
            badgeNode?.classList.remove('hidden');
            badgeNode?.classList.add('title-container__badge--no-hover');
            badgeInitialsVerifiedNode?.classList.add(
                'badge-initials-verified__centered',
            );
            badgeExtraNameNode.classList.remove('badge-extra-name__bold');

            badgeInitialsTextNode.innerHTML = '';
            badgeExtraNameNode.innerHTML = 'Real person';
            badgeExtraRoleNode.innerHTML = '';

            // Remove focusability when no nameplate
            badgeNode?.removeAttribute('tabindex');
            badgeNode?.removeAttribute('role');
            badgeNode?.removeAttribute('aria-label');
        }
    }

    function replaceCurrentVideo(data: FlowConfigPlayerVideo) {
        var videoId = data.id;
        var mediaUrl = data.url;
        var subtitlesUrl = data.subtitles;
        var buttons = data.buttons;
        var form = data.form;
        var playbackRate = data.playbackRate || 1;
        const buttonPresentationDelay = data.buttonPresentationDelay;

        if (!videoNode || !buttonContainerNode || !replayContainerNode) {
            return;
        }

        videoNode.innerHTML = '';

        // NOTE: Reset the flag for new video or restart
        isVideoStartedTracked = false;

        videoNode.src = mediaUrl;
        videoNode.currentTime = 0;

        buttonContainerNode
            .querySelectorAll(
                '.action-button, .action-form-input, .action-form-block',
            )
            .forEach((element) => {
                element.remove();
            });

        // NOTE: If there is a button presentation delay,
        //   hide the buttons container to animate it later
        if (buttonPresentationDelay) {
            // buttonContainerNode.style.transition = 'unset';
            buttonContainerNode.style.opacity = '0';

            if (captionTextNode) {
                captionTextNode.style.transition = 'unset';
            }
        }

        buttons.forEach(renderButton);

        if (buttonPresentationDelay && captionTextNode) {
            captionTextNode.style.transform = `translateY(${getCaptionTextVerticalOffset()}px)`;
        }

        if (form) {
            // TODO: Render form
        }

        var nextTrackNode = document.createElement('track');

        nextTrackNode.src = subtitlesUrl;
        nextTrackNode.label = 'English';
        nextTrackNode.kind = 'subtitles';
        nextTrackNode.srclang = 'en';
        nextTrackNode.default = false;

        videoNode.appendChild(nextTrackNode);

        const nextTextTrack = videoNode.textTracks[0];

        nextTextTrack.mode = 'hidden';

        nextTextTrack.oncuechange = function () {
            onTrackCueChange(nextTextTrack);
        };

        flowState.currentVideoId = videoId;
        flowState.extra.isButtonListAnimated = false;

        if (!flowState.videoIdHistory.includes(videoId)) {
            flowState.videoIdHistory.push(videoId);
        }

        hideReplay();

        // clearTimeout(repeatTimeout);

        videoNode.play();

        flowState.controlMap.isPlaying = true;
        videoNode.muted = flowState.controlMap.isMuted;

        videoNode.playbackRate = playbackRate;

        renderHistoryBar();

        if (flowConfig.type === 'inline') {
            onResizePlayer();
        }

        // if (
        //     flowConfigPlayer.themeVariant === 2 ||
        //     (flowConfigPlayer.themeVariant === 3 && getIsDark())
        // ) {
        //     changeColorTheme(2);
        // }

        renderBadge(data);
    }

    function setMuteButtonIcon(isMuted: boolean) {
        if (!muteButtonNode) {
            return;
        }

        const isTooltipsEnabled = false;

        muteButtonNode.setAttribute('data-muted', `${isMuted}`);
        muteButtonNode.setAttribute('aria-pressed', `${isMuted}`);
        muteButtonNode.setAttribute(
            'aria-label',
            isMuted ? 'Unmute video' : 'Mute video',
        );

        let muteButtonIcon = isMuted
            ? getIcon('mute', { title: '', color: 'white' })
            : getIcon('unmute', { title: '', color: 'white' });

        muteButtonIcon = `<div class="control-button-content">${muteButtonIcon}</div>`;

        var isCompact = flowConfigPlayer.sizeVariant === 1;

        // TODO: Define as an element not inline HTML
        var tooltipRawHtml = `<div class="mute-tooltip" style="${
            isCompact ? 'font-size:12px' : ''
        }">${isMuted ? 'Turn sound on' : 'Turn sound off'}</div>`;

        muteButtonNode.innerHTML =
            muteButtonIcon + (isTooltipsEnabled ? tooltipRawHtml : '');
    }

    function showLoader() {
        if (loaderContainerNode) {
            loaderContainerNode.style.display = 'flex';
            loaderContainerNode.style.visibility = 'visible';
        }
    }

    function hideLoader() {
        if (loaderContainerNode) {
            loaderContainerNode.style.display = 'none';
            loaderContainerNode.style.visibility = 'hidden';
        }
    }

    function showReplay() {
        if (!replayContainerNode) {
            return;
        }

        replayContainerNode.style.display = 'flex';
        replayContainerNode.style.visibility = 'visible';
        replayContainerNode.tabIndex = 0;
    }

    function hideReplay() {
        if (!replayContainerNode) {
            return;
        }

        replayContainerNode.style.display = 'none';
        replayContainerNode.style.visibility = 'hidden';
        replayContainerNode.tabIndex = 1;
    }

    function replayVideo(event: MouseEvent) {
        var currentVideoConfig = flowConfigPlayer.videos.find(function (data) {
            return data.id === flowState.currentVideoId;
        });

        var playbackRate =
            currentVideoConfig && currentVideoConfig.playbackRate
                ? currentVideoConfig.playbackRate
                : 1;

        if (!videoNode) {
            return;
        }

        if (
            !flowState.controlMap.isPlaying &&
            flowState.extra.currentTime &&
            flowState.extra.duration &&
            flowState.extra.currentTime === flowState.extra.duration
        ) {
            eventTracker.track('player_video_replay');

            hideReplay();

            videoNode.currentTime = 0;
            videoNode.muted = false;
            videoNode.play();
            videoNode.playbackRate = playbackRate;

            flowState.controlMap.isPlaying = true;
            flowState.controlMap.isMuted = !!videoNode.muted;

            isMuteStateChangedMap[flowConfig.code] = true;

            setMuteButtonIcon(videoNode.muted);
            cachePlayerMuteState();
        } else if (
            (!flowState.controlMap.isPlaying &&
                flowState.extra.currentTime &&
                flowState.extra.duration &&
                flowState.extra.currentTime !== flowState.extra.duration) ||
            (!flowState.controlMap.isPlaying &&
                !flowState.extra.currentTime &&
                !flowState.extra.duration &&
                flowConfig.type === 'inline')
        ) {
            eventTracker.track('player_play', { source: 'button' });

            videoNode.muted = flowState.controlMap.isMuted;

            videoNode.play();

            videoNode.playbackRate = playbackRate;

            hideReplay();

            flowState.controlMap.isPlaying = true;

            setMuteButtonIcon(videoNode.muted);
        }
    }

    function playVideo() {
        const currentVideoConfig = getCurrentVideoConfig();

        videoNode.playbackRate =
            currentVideoConfig && currentVideoConfig.playbackRate
                ? currentVideoConfig.playbackRate
                : 1;

        videoNode?.play();

        flowState.controlMap.isPlaying = true;

        if (isMuteStateChangedMap[flowConfig.code]) {
            videoNode.muted = flowState.controlMap.isMuted;
        } else {
            videoNode.muted = true;
        }

        setMuteButtonIcon(videoNode.muted);
    }

    function onVideoClick(source?: 'keyboard') {
        if (!videoNode) {
            return;
        }

        if (flowState.controlMap.isPlaying) {
            eventTracker.track(
                'player_pause',
                source ? { source: source } : undefined,
            );

            videoNode.pause();

            flowState.controlMap.isPlaying = false;
        } else {
            eventTracker.track(
                'player_play',
                source ? { source: source } : undefined,
            );

            videoNode.muted = isMuteStateChangedMap[flowConfig.code]
                ? flowState.controlMap.isMuted
                : false;

            videoNode.play();

            const currentVideoConfig = flowConfigPlayer.videos.find(
                function (data) {
                    return data.id === flowState.currentVideoId;
                },
            );

            const playbackRate =
                currentVideoConfig && currentVideoConfig.playbackRate
                    ? currentVideoConfig.playbackRate
                    : 1;

            videoNode.playbackRate = playbackRate;

            flowState.controlMap.isPlaying = true;

            setMuteButtonIcon(videoNode.muted);
        }
    }

    function getCaptionTextVerticalOffset() {
        if (!buttonContainerNode) {
            return 0;
        }

        const buttonHeightPx = 41;
        const childElementCount = buttonContainerNode.children.length;

        return childElementCount * buttonHeightPx;
    }

    function getButtonAnimationDelayInSeconds() {
        const currentVideo = flowConfigPlayer.videos.find(
            (video) => video.id === flowState.currentVideoId,
        );

        const buttonPresentationDelay = currentVideo?.buttonPresentationDelay;

        const videoDuration = videoNode.duration;

        let animationDelay = 0;

        if (typeof buttonPresentationDelay === 'number') {
            if (buttonPresentationDelay > 0) {
                animationDelay = buttonPresentationDelay;

                if (animationDelay > videoDuration) {
                    animationDelay = videoDuration;
                }
            } else {
                animationDelay = videoDuration + buttonPresentationDelay;

                if (animationDelay < 0) {
                    animationDelay = videoDuration;
                }
            }
        }

        return animationDelay;
    }

    function getIsReadyToAnimateButtonList(currentTime: number) {
        const currentVideo = flowConfigPlayer.videos.find(
            (video) => video.id === flowState.currentVideoId,
        );

        const buttonPresentationDelay = currentVideo?.buttonPresentationDelay;

        if (typeof buttonPresentationDelay !== 'number') {
            // NOTE: Animate immediately if no value provided
            return true;
        } else {
            const animDelay = getButtonAnimationDelayInSeconds();

            return currentTime >= animDelay;
        }
    }

    function onButtonListAnimationFinish() {
        if (!buttonContainerNode || !captionTextNode) {
            return;
        }

        buttonContainerNode.style.opacity = '1';
        buttonContainerNode.style.pointerEvents = 'auto';

        // NOTE: Move captions back to normal position
        captionTextNode.style.transform = 'translateY(0)';

        buttonListShowAnimExecutor = null;
    }

    function resetButtonListAnimation() {
        if (!buttonContainerNode || !captionTextNode) {
            return;
        }

        buttonContainerNode.style.opacity = '0';
        buttonContainerNode.style.pointerEvents = 'none';

        if (getButtonAnimationDelayInSeconds() > 0) {
            captionTextNode.style.transform = `translateY(${getCaptionTextVerticalOffset()}px)`;
        } else {
            captionTextNode.style.transform = `0`;
        }

        buttonListShowAnimExecutor = null;
    }

    function animateButtonList(videoDuration: number) {
        // Get current video config to check for button presentation delay
        const currentVideo = flowConfigPlayer.videos.find(
            (video) => video.id === flowState.currentVideoId,
        );
        const buttonPresentationDelay = currentVideo?.buttonPresentationDelay;

        function _animateButtonList(buttonPresentationDelay?: number) {
            if (!buttonContainerNode || !captionTextNode) {
                return;
            }

            // NOTE: Initially hide buttons and shift captions down
            captionTextNode.style.transition = 'transform 0.18s ease-in';
            buttonContainerNode.style.opacity = '0';
            buttonContainerNode.style.pointerEvents = 'none';

            if (buttonPresentationDelay) {
                captionTextNode.style.transform = `translateY(${getCaptionTextVerticalOffset()}px)`;
            }

            buttonListShowAnimExecutor = buttonContainerNode.animate(
                {
                    opacity: [0, 1],
                },
                {
                    duration: 300,
                    iterations: 1,
                },
            );

            buttonListShowAnimExecutor.addEventListener(
                'finish',
                onButtonListAnimationFinish,
            );

            buttonListShowAnimExecutor.addEventListener(
                'cancel',
                onButtonListAnimationFinish,
            );
        }

        if (buttonListShowAnimExecutor) {
            // NOTE: Cancel any ongoing animation
            buttonListShowAnimExecutor.cancel();
        }

        _animateButtonList(buttonPresentationDelay);
    }

    function animateNamePlateContainer(payload: {
        authorName: string;
        authorRole: string;
    }) {
        const authorName = payload.authorName;
        const authorRole = payload.authorRole;

        if (!buttonContainerNode || !badgeNode || !captionTextNode) {
            return;
        }

        // If name hasn't been viewed yet, show the extra container
        if (
            authorName &&
            authorRole &&
            !getIsNameViewed(authorName, authorRole)
        ) {
            // Add class to trigger the extra container animation
            badgeNode.classList.add('title-container__badge--show-extra');

            // Set a timeout to remove the class after animation completes
            setTimeout(() => {
                badgeNode?.classList.remove(
                    'title-container__badge--show-extra',
                );

                setIsNameViewed(authorName, authorRole);
            }, 3000); // Match the total animation duration
        }
    }

    function onResizePlayer() {
        const buttonListEl = document.querySelector<HTMLDivElement>(
            BUTTON_CONTAINER_SELECTOR,
        );

        if (!buttonListEl) {
            return;
        }

        const buttonListElCopy = buttonListEl.cloneNode(true) as HTMLDivElement;

        if (!buttonListEl.classList.contains('wrapped-content')) {
            if (getIsContentWrapped(buttonListEl)) {
                buttonListEl.classList.add('wrapped-content');
            }
        } else {
            buttonListElCopy.classList.remove('wrapped-content');
            buttonListElCopy.style.opacity = '0';
            buttonListElCopy.style.zIndex = '-1';

            document.body.append(buttonListElCopy);

            if (!getIsContentWrapped(buttonListElCopy)) {
                buttonListEl.classList.remove('wrapped-content');
            }

            buttonListElCopy.remove();
        }
    }

    function onIntersectionChange(
        payload: PlayerIntersectionChangeMessagePayload,
    ) {
        if (
            (payload.isIntersecting && flowState.controlMap.isPlaying) ||
            (!payload.isIntersecting && !flowState.controlMap.isPlaying)
        ) {
            return;
        }

        const isPauseVideoOnIntersectionLossEnabled = false;

        if (
            payload.isIntersecting &&
            !flowState.controlMap.isPlaying &&
            flowConfigPlayer.autoPlay &&
            //
            // NOTE: Player should not auto-play more than once during the page view
            //
            // NOTE: [ChatGPT]
            // Chrome may set "video.currentTime" slightly above 0 (e.g., 0.007) when playback starts due to:
            //   - Minor timing variances in buffering and decoding
            //   - Floating-point precision limits in JavaScript
            // Using a small threshold (e.g., currentTime < 1) handles these discrepancies across browsers.
            //
            flowState.extra.currentTime < 1
        ) {
            if (
                flowConfigPlayer.autoPlayDelay &&
                flowConfigPlayer.autoPlayDelay > 0
            ) {
                const timerId = setTimeout(
                    playVideo,
                    flowConfigPlayer.autoPlayDelay * 1000,
                );

                autoPlayerTimers.push(timerId);
            } else {
                playVideo();
            }
        } else if (
            !payload.isIntersecting &&
            flowState.controlMap.isPlaying &&
            isPauseVideoOnIntersectionLossEnabled
        ) {
            videoNode.pause();

            autoPlayerTimers.forEach((timerId) => {
                clearTimeout(timerId);
            });

            autoPlayerTimers = [];
        }
    }

    function getCurrentVideoConfig() {
        var currentVideoConfig = flowConfigPlayer.videos.find(function (data) {
            return data.id === flowState.currentVideoId;
        });

        return currentVideoConfig;
    }

    function onVideoClose(source: 'button' | 'keyboard' | 'api') {
        eventTracker.track('player_close', { source });

        postMessage(PLAYER_CLOSE_MESSAGE_TYPE);

        videoNode.pause();

        videoNode.currentTime = 0;

        // NOTE: Reset the flag for new video or restart
        isVideoStartedTracked = false;

        flowState.extra.isButtonListAnimated = false;

        resetButtonListAnimation();
    }

    var currentVideo = flowConfigPlayer.videos.find(function (video) {
        return video.id === flowState.currentVideoId;
    }) as FlowConfigPlayerVideo;

    titleNode.innerHTML = matchTemplateStr(
        currentVideo.title,
        pageMetadata.url,
    );

    currentVideo.buttons.forEach(renderButton);

    // NOTE: This is already done in "replaceCurrentVideo" function, but we don't call it for the first player open
    if (currentVideo.buttonPresentationDelay) {
        captionTextNode.style.transform = `translateY(${getCaptionTextVerticalOffset()}px)`;
    }

    renderHistoryBar();
    // replaceCurrentVideo(currentVideo);
    renderBadge(currentVideo);

    const videoTextTrack = videoNode.textTracks[0];

    videoTextTrack.mode = 'hidden';

    videoTextTrack.oncuechange = function () {
        onTrackCueChange(videoTextTrack);
    };

    videoNode.ontimeupdate = function () {
        if (!videoNode) {
            return;
        }

        if (typeof videoNode.currentTime === 'number') {
            flowState.extra.currentTime = videoNode.currentTime;
        }

        if (typeof videoNode.duration === 'number') {
            flowState.extra.duration = videoNode.duration;
        }

        if (
            !flowState.extra.isButtonListAnimated &&
            getIsReadyToAnimateButtonList(videoNode.currentTime)
        ) {
            animateButtonList(videoNode.duration);

            flowState.extra.isButtonListAnimated = true;
        }

        if (!flowState.controlMap.isPlaying) {
            return;
        }

        if (
            videoNode &&
            muteButtonNode.getAttribute('data-muted') !== `${videoNode.muted}`
        ) {
            setMuteButtonIcon(videoNode.muted);
        }

        var headerElement = document.querySelector(HISTORY_CONTAINER_SELECTOR);

        var historyActiveButtonElement = headerElement?.querySelector(
            'button[data-video-id="' + flowState.currentVideoId + '"]',
        );

        if (!historyActiveButtonElement) {
            return null;
        }

        historyActiveButtonElement.setAttribute(
            'aria-valuenow',
            ((videoNode.currentTime / videoNode.duration) * 100).toFixed(2),
        );

        var progressBarElement =
            historyActiveButtonElement.querySelector<HTMLDivElement>(
                '.history-button-progress-line',
            );

        var videoProgress = (videoNode.currentTime / videoNode.duration) * 100;

        if (progressBarElement) {
            progressBarElement.style.transform =
                'translateX(-' + (100 - videoProgress) + '%)';
        }
    };

    // NOTE: The event fires whenever a new video file is loaded into the player
    videoNode.onloadedmetadata = function () {
        // NOTE: Reset the flag for new video or restart
        isVideoStartedTracked = false;
    };

    videoNode.onwaiting = function (event) {
        flowState.controlMap.isPlaying = false;

        showLoader();
    };

    videoNode.onplaying = function (event) {
        flowState.controlMap.isPlaying = true;

        hideLoader();

        //
        // NOTE: For some reason in Chrome, when switching between videos (e.g. open video by clicking action button),
        //   the "currentTime" value is slightly above 0 (e.g. 0.007242, 0.009947, etc.). Because of this, we add a
        //   threshold of 1 seconds in which we consider the video "_started" playing and the rest is "_resumed".
        //
        // NOTE: [ChatGPT]
        // Chrome may set "video.currentTime" slightly above 0 (e.g., 0.007) when playback starts due to:
        //   - Minor timing variances in buffering and decoding
        //   - Floating-point precision limits in JavaScript
        // Using a small threshold (e.g., currentTime < 0.1) handles these discrepancies across browsers.
        //
        if (!isVideoStartedTracked && videoNode.currentTime < 1) {
            isVideoStartedTracked = true;

            eventTracker.track('player_video_started', {
                video_id: flowState.currentVideoId,
                current_time: videoNode.currentTime,
            });
        }
    };

    videoNode.onpause = function () {
        showReplay();

        flowState.controlMap.isPlaying = false;
    };

    videoNode.onplay = function (event) {
        hideReplay();

        flowState.controlMap.isPlaying = true;

        var targetVideo = event.target as HTMLVideoElement;

        var isVideoStartEvent =
            !isVideoStartedTracked && videoNode.currentTime < 1;

        if (!isVideoStartEvent) {
            eventTracker.track('player_video_resumed', {
                video_id: flowState.currentVideoId,
                current_time: videoNode.currentTime,
            });
        }

        // In Chrome (and perhaps other browsers), if video is muted, and browser windows loses focus,
        //   the browser is pausing video, so when user focuses browser window again the video resumes,
        //   and "play" event gets called again. Because of that we have to check "currentTime" before animation
        if (
            targetVideo &&
            typeof targetVideo.currentTime === 'number' &&
            targetVideo.currentTime < 1
        ) {
            var currentVideo = flowConfigPlayer.videos.find(
                (el) => el.id === flowState.currentVideoId,
            );

            if (!currentVideo) {
                return;
            }

            animateNamePlateContainer({
                authorName: currentVideo.authorName || '',
                authorRole: currentVideo.authorRole || '',
            });

            if (
                !flowState.extra.isButtonListAnimated &&
                getIsReadyToAnimateButtonList(videoNode.currentTime)
            ) {
                animateButtonList(videoNode.duration);

                flowState.extra.isButtonListAnimated = true;
            }
        }
    };

    videoNode.onclick = function () {
        if (!isVideoMouseDownEffectRunning) {
            onVideoClick();
        } else {
            isVideoMouseDownEffectRunning = false;
        }
    };

    videoNode.onended = function () {
        eventTracker.track('player_video_ended', {
            _payloadVersion: 2,
            video_id: flowState.currentVideoId,
            current_time: videoNode.currentTime,
        });

        if (flowState.extra.currentTime !== flowState.extra.duration) {
            // Set current time to duration, so we can identify the state (ended)
            flowState.extra.currentTime = flowState.extra.duration;
        }

        flowState.controlMap.isPlaying = false;

        videoNode.muted = true; // Mute at end of playback to avoid browser autoplay restrictions and annoying the user

        showReplay();
    };

    // NOTE: This event will not fire in mobile/tablet devices if data-saver is on in browser settings
    // See more: https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/loadeddata_event
    videoNode.onloadeddata = function () {
        eventTracker.track('player_video_loaded', {
            _payloadVersion: 2,
            video_id: flowState.currentVideoId,
        });
    };

    function startSpeedModeTimer() {
        if (videoMouseDownTimerId) {
            clearTimeout(videoMouseDownTimerId);

            videoMouseDownTimerId = null;
        }

        videoMouseDownTimerId = setTimeout(function () {
            videoMouseDownTimerId = null;
            isVideoMouseDownEffectRunning = true;

            const currentVideoConfig = getCurrentVideoConfig();
            const defaultPlaybackRate =
                currentVideoConfig && currentVideoConfig.playbackRate
                    ? currentVideoConfig.playbackRate
                    : 1;

            videoNode.playbackRate = defaultPlaybackRate * 2;

            hideElementsForSpeedMode();
        }, 250);
    }

    function stopSpeedModeTimer() {
        if (videoMouseDownTimerId) {
            clearTimeout(videoMouseDownTimerId);

            videoMouseDownTimerId = null;
        }

        const currentVideoConfig = getCurrentVideoConfig();
        const defaultPlaybackRate =
            currentVideoConfig && currentVideoConfig.playbackRate
                ? currentVideoConfig.playbackRate
                : 1;

        videoNode.playbackRate = defaultPlaybackRate;

        showElements();

        // NOTE: No need to reset the flag, because we do it in the "videoNode.onclick" handler
        // isVideoMouseDownEffectRunning = false;
    }

    videoNode.onmousedown = startSpeedModeTimer;
    videoNode.onmouseup = stopSpeedModeTimer;
    videoNode.ontouchstart = startSpeedModeTimer;
    videoNode.ontouchend = stopSpeedModeTimer;
    videoNode.onmouseleave = stopSpeedModeTimer;

    videoNode.onerror = function (event) {
        eventTracker.track('error', {
            name: 'player_video_load',
            // @ts-expect-error
            message: event.message || '',
        });
    };

    replayContainerNode.addEventListener('click', replayVideo);

    closeButtonNode.onclick = function (event) {
        onVideoClose('button');
    };

    muteButtonNode.onclick = function (event) {
        if (videoNode.muted) {
            eventTracker.track('player_unmute');
        } else {
            eventTracker.track('player_mute');
        }

        videoNode.muted = !videoNode.muted; // Toggle

        flowState.controlMap.isMuted = !!videoNode.muted;

        isMuteStateChangedMap[flowConfig.code] = true;

        setMuteButtonIcon(videoNode.muted);
        cachePlayerMuteState();
    };

    goBackButtonNode.onclick = function (event) {
        const currentVideoHistoryIndex = flowState.videoIdHistory.findIndex(
            (videoId) => videoId === flowState.currentVideoId,
        );

        const isFirstVideo = currentVideoHistoryIndex === 0;

        const isVideoFinished =
            flowState.extra.currentTime &&
            flowState.extra.duration &&
            flowState.extra.currentTime === flowState.extra.duration;

        let videoData = null;

        if (isFirstVideo || isVideoFinished) {
            videoData = flowConfigPlayer.videos.find(
                (item) => item.id === flowState.currentVideoId,
            );
        } else {
            // In case there is a previous video - choose it, otherwise use current video
            var targetVideoIndex =
                currentVideoHistoryIndex - 1 >= 0
                    ? currentVideoHistoryIndex - 1
                    : currentVideoHistoryIndex;

            videoData = flowConfigPlayer.videos[targetVideoIndex];
        }

        if (!videoData) {
            return;
        }

        eventTracker.track('player_go_back');

        replaceCurrentVideo(videoData);
    };

    buttonContainerNode.addEventListener('click', (event) => {
        if (!event || !event.target) {
            return;
        }

        // @ts-expect-error
        var actionId = event.target.getAttribute('data-action-id');
        // @ts-expect-error
        var label = event.target.innerHTML;

        var currentVideo = flowConfigPlayer.videos.find(
            (videoItem) => videoItem.id === flowState.currentVideoId,
        );

        if (!currentVideo) {
            // TODO: Log error
            // eventTracker.track("error", { /* ... */ });

            logDebug('[ReelFlow] Cannot find current video', {
                actionId,
                label,
            });

            return;
        }

        var buttonData = currentVideo.buttons.find(
            (buttonItem) => buttonItem.id === actionId,
        );

        var buttonIndex = currentVideo.buttons.findIndex(
            (buttonItem) => buttonItem.id === actionId,
        );

        if (!buttonData) {
            // TODO: Log error
            // eventTracker.track("error", { /* ... */});

            logDebug('[ReelFlow] Cannot find button data', {
                actionId,
                label,
                currentVideo,
            });

            return;
        }

        eventTracker.track('player_action_button', {
            _payloadVersion: 2,
            id: actionId,
            label,
            video_id: flowState.currentVideoId,
        });

        postMessage(ACTION_BUTTON_CLICK_MESSAGE_TYPE, {
            id: actionId,
            label,
            index: buttonIndex,
        });

        const triggerPlayerCloseAction = () => {
            closeButtonNode.click();
        };

        if (
            buttonData.actionType === 'a' &&
            buttonData.actionPayload &&
            buttonData.actionPayload.href &&
            typeof buttonData.actionPayload.href === 'string' &&
            buttonData.actionPayload.href.includes('#')
        ) {
            const targetPageUrl = new URL(buttonData.actionPayload.href);
            const currentPageUrl = new URL(pageMetadata.url);

            const currentPage = currentPageUrl.origin + currentPageUrl.pathname;
            const targetPage = targetPageUrl.origin + targetPageUrl.pathname;

            if (currentPage === targetPage) {
                const elementId = targetPageUrl.hash;

                if (!elementId) {
                    if (buttonData.actionPayload.hidePlayerOnNavigate) {
                        triggerPlayerCloseAction();
                    }

                    return;
                }

                event.preventDefault();

                const payload: PageScrollMessagePayload = {
                    elementId,
                    // @ts-expect-error
                    scrollStateDelay: buttonData.actionPayload.scrollStateDelay,
                    // @ts-expect-error
                    scrollBehavior: buttonData.actionPayload.scrollBehavior,
                    // @ts-expect-error
                    scrollBlock: buttonData.actionPayload.scrollBlock,
                    // @ts-expect-error
                    scrollInline: buttonData.actionPayload.scrollInline,
                };

                postMessage(PAGE_SCROLL_REQUEST_MESSAGE_TYPE, payload);
            }

            const nextVideoId = buttonData.actionPayload.videoId;

            if (nextVideoId) {
                const targetVideo = flowConfigPlayer.videos.find(
                    (videoItem) => videoItem.id === nextVideoId,
                );

                if (targetVideo) {
                    replaceCurrentVideo(targetVideo);
                }
            }

            if (buttonData.actionPayload.hidePlayerOnNavigate) {
                triggerPlayerCloseAction();
            }
        } else if (buttonData.actionType === OPEN_VIDEO_ACTION_TYPE) {
            var targetFlowId = buttonData.actionPayload.flowId;
            var targetVideoId = buttonData.actionPayload.videoId;

            var targetVideo = flowConfigPlayer.videos.find(
                (videoItem) => videoItem.id === targetVideoId,
            );

            if (targetVideo) {
                replaceCurrentVideo(targetVideo);
            }
        } else if (
            buttonData.actionType === HIDE_PLAYER_ACTION_TYPE ||
            (buttonData.actionPayload &&
                buttonData.actionPayload.hidePlayerOnNavigate)
        ) {
            triggerPlayerCloseAction();
        }
    });

    messageEventListenerMap[VIDEO_RENDER_LISTENER_ID] = function (event) {
        if (event.type === PLAYER_OPEN_MESSAGE_TYPE) {
            playVideo();
        } else if (event.type === METADATA_CHANGE_MESSAGE_TYPE) {
            if (flowConfig.type === 'inline') {
                // TODO: Check if the size has change (prev metadata to what received in the event)
                onResizePlayer();
            }
        } else if (event.type === PLAYER_INTERSECTION_CHANGE_MESSAGE_TYPE) {
            const payload =
                event.data as PlayerIntersectionChangeMessagePayload;

            onIntersectionChange(payload);
        } else if (event.type === PLAYER_CLOSE_REQUEST_MESSAGE_TYPE) {
            onVideoClose('api');
        }
    };

    keyDownListenerMap[ESCAPE_KEYBOARD_LISTENER_ID] = function (event) {
        onVideoClose('keyboard');
    };

    keyDownListenerMap[SPACE_KEYBOARD_LISTENER_ID] = function (event) {
        const isControlButtonFocused =
            document.activeElement === muteButtonNode ||
            document.activeElement === closeButtonNode;

        const isAnyHeaderButtonFocused = Array.from(
            document.querySelectorAll('.history-button'),
        ).some((button) => document.activeElement === button);

        const isAnyActionButtonFocused = Array.from(
            document.querySelectorAll('.action-button'),
        ).some((button) => document.activeElement === button);

        // If user clicks on the Space whilst other buttons are focused (controls, action buttons, etc.)
        //   no need to handle the action since most likely it should be handled automatically
        //   (e.g. clicking on close closes the player)
        if (
            isControlButtonFocused ||
            isAnyHeaderButtonFocused ||
            isAnyActionButtonFocused
        ) {
            return;
        } else {
            onVideoClick('keyboard');
        }
    };

    keyDownListenerMap[K_KEYBOARD_LISTENER_ID] = function (event) {
        onVideoClick('keyboard');
    };

    videoPlaceholderNode.replaceWith(videoNode);

    if (flowConfig.type === 'inline') {
        onResizePlayer();
        showReplay();
    }
}

function setupMessageListener() {
    window.addEventListener('message', function (event) {
        const workspaceUrl = new URL(workspace.url);

        const normalizedOrigin = normalizeUrl(event.origin);
        const normalizedWorkspaceOrigin = normalizeUrl(workspaceUrl.origin);

        if (normalizedOrigin !== normalizedWorkspaceOrigin) {
            logDebug('[ReelFlow] Unknown domain, ignore the message', {
                event,
                url: workspace.url,
                normalizedOrigin,
                normalizedWorkspaceOrigin,
            });

            console.error(
                'ReelFlow Install Code issue: the Workspace URL does not match this domain.',
            );

            return;
        }

        // NOTE: The parent page (workspace URL) can send its own messages to our iframe,
        //   so if it's not a valid JSON (what we expect), ignore the message
        const eventData = (() => {
            try {
                return JSON.parse(event.data);
            } catch (ex) {
                logDebug('[ReelFlow] Error parsing message', { event });
            }

            return null;
        })();

        if (!eventData) {
            return;
        }

        logDebug('[ReelFlow] New message received', {
            event,
            payload: eventData,
        });

        messageHistory.push({ message: eventData, receivedAt: Date.now() });

        Object.keys(messageEventListenerMap).forEach((key) => {
            const onMessage = messageEventListenerMap[key as MessageListenerId];

            onMessage?.(eventData);
        });
    });
}

function postMessage(type: MessageType, data?: Message<any>['data']) {
    try {
        const payload = {
            type,
            data: {
                ...(data || {}),
                flowId: flowConfig.code,
            },
        };

        window.parent.postMessage(JSON.stringify(payload), workspace.url);
    } catch (ex) {
        logDebug('[ReelFlow] Cannot post message', { type, data });
    }
}

function setupKeyDownListener() {
    document.addEventListener('keydown', function (event) {
        const listenerKeyCodeMap: Record<KeyDownListenerId, number | number[]> =
            {
                [ESCAPE_KEYBOARD_LISTENER_ID]: 27,
                [SPACE_KEYBOARD_LISTENER_ID]: 32,
                [K_KEYBOARD_LISTENER_ID]: 75,
            };

        Object.keys(listenerKeyCodeMap).forEach((id) => {
            const listenerId = id as KeyDownListenerId;

            if (
                typeof listenerKeyCodeMap[listenerId] === 'number' &&
                listenerKeyCodeMap[listenerId] === event.keyCode
            ) {
                keyDownListenerMap[listenerId]?.(event);
            }
        });
    });
}

function hideElementsForSpeedMode() {
    const elementsToHide = [
        document.querySelector(CONTROLS_CONTAINER_SELECTOR),
        document.querySelector(HISTORY_CONTAINER_SELECTOR),
        document.querySelector(TITLE_SELECTOR),
        document.querySelector(NAME_PLATE_CONTAINER_SELECTOR),
        document.querySelector(BADGE_SELECTOR),
        document.querySelector(BUTTON_CONTAINER_SELECTOR),
    ];

    elementsToHide.forEach((element) => {
        if (element) {
            element.classList.add('speed-mode-hidden');
        }
    });

    const speedIndicator = document.querySelector(SPEED_INDICATOR_SELECTOR);

    if (speedIndicator) {
        speedIndicator.classList.remove('hidden');
    }

    playerStateMap[flowConfig.code].extra.uiElementsHidden = true;
}

function showElements() {
    if (!playerStateMap[flowConfig.code].extra.uiElementsHidden) {
        return;
    }

    const speedIndicator = document.querySelector(SPEED_INDICATOR_SELECTOR);

    if (speedIndicator) {
        speedIndicator.classList.add('hidden');
    }

    document.querySelectorAll('.speed-mode-hidden').forEach((element) => {
        element.classList.remove('speed-mode-hidden');
    });

    playerStateMap[flowConfig.code].extra.uiElementsHidden = false;
}

(function () {
    initializePlayerState();

    messageEventListenerMap[MAIN_LISTENER_ID] = function (
        message: Message<PageMetadata>,
    ) {
        if (
            ![FRAME_INIT_MESSAGE_TYPE, METADATA_CHANGE_MESSAGE_TYPE].includes(
                message.type,
            )
        ) {
            return;
        }

        if (pageMetadata?.isDesktop !== message.data.isDesktop) {
            if (message.data.isDesktop) {
                document.documentElement.setAttribute('data-is-desktop', '1');
            } else {
                document.documentElement.removeAttribute('data-is-desktop');
            }
        }

        pageMetadata = message.data;

        // NOTE: The INIT message should fire only once
        if (message.type === FRAME_INIT_MESSAGE_TYPE) {
            const pageUrl = pageMetadata.url;

            const urlData = new URL(pageUrl);

            const source = urlData.origin + urlData.pathname;

            initializeEventTracker({
                apiUrl: `https://${window.location.hostname}/api/`,
                workspaceId: workspace.code,
                flowId: `${flowConfig.id}`,
                isPreviewMode: pageMetadata.isPreviewMode,
                source: source,
            });

            // NOTE: Render the first video only when metadata received
            renderVideo();
        }
    };

    setupMessageListener();

    setupKeyDownListener();

    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
            getEventTracker().submit();
        }
    });
})();
